import { Suspense, type JSX, useState } from "react";
import { lazyPage } from "@web/shared/lib/lazy-page";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Button,
  Icon,
  Ltr,
  MenuItem,
  PersonName,
  PhoneLink,
  RowMenu,
  TabPanel,
  Tabs,
  useConfirm,
  useTabParam,
  useToast,
  WhatsAppLink,
} from "@clinic/ui";
import { Skeleton, SkeletonStatus } from "@clinic/ui/components/skeleton";
import { AppointmentFormModal } from "@web/modules/appointments/components/appointment-form-modal";
import { canBookAppointment } from "@web/shared/permissions/appointments";
import { useSession } from "@web/shared/providers/session";
import { AccountTab } from "@web/modules/billing/pages/account-tab";
import { PatientBalanceCard } from "@web/modules/billing/components/patient-balance-card";
import { canSeeBilling } from "@web/shared/permissions/billing";
import { ageInYears } from "@web/modules/patients/lib/age";
import { AllergyBanner } from "@web/modules/patients/components/allergy-banner";
import { PatientFormModal } from "@web/modules/patients/components/patient-form-modal";
import { canDeletePatient, canEditPatient, canViewChart } from "@web/shared/permissions/patients";
import { PrescriptionsTab } from "@web/modules/patients/pages/prescriptions-tab";
import { useDeletePatient, usePatient } from "@web/modules/patients/queries";
import { TimelineTab } from "@web/modules/patients/pages/timeline-tab";
import { TreatmentPlansTab } from "@web/modules/patients/pages/treatment-plans-tab";
import { VisitsTab } from "@web/modules/patients/pages/visits-tab";
import { useDelayedLoading } from "@clinic/ui/lib/use-delayed-loading";
import { errorToast } from "@web/shared/lib/api-error";
import { whatsAppNumber } from "@web/shared/lib/whatsapp";
import { useDoctors } from "@web/shared/queries/doctors";
import { PATIENT_TABS, PATIENT_TAB_PARAMS } from "@web/modules/patients/constants";
import { chartTabLabel, patientCharts } from "@web/modules/patients/lib/chart/charts";

const ChartTab = lazyPage(async () => ({
  default: (await import("@web/modules/patients/pages/chart-tab")).ChartTab,
}));

const ImagingTab = lazyPage(async () => ({
  default: (await import("@web/modules/patients/pages/imaging-tab")).ImagingTab,
}));

type TabId = (typeof PATIENT_TABS)[number]["id"];

export function PatientPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, can } = useSession();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const deletePatient = useDeletePatient();

  const role = user?.role;
  const charted = patientCharts(user?.clinic.chartTypes ?? []).length > 0;
  const tabs = PATIENT_TABS.filter(
    (tab) =>
      (tab.id !== "chart" || charted) &&
      (tab.clinical ? role && canViewChart(can) && can(tab.reads) : role && canSeeBilling(can)),
  );

  const [activeTab, setActiveTab] = useTabParam<TabId>(
    "tab",
    tabs.map((tab) => tab.id),
    tabs[0]?.id ?? "billing",
    PATIENT_TAB_PARAMS,
  );

  const patient = usePatient(id);
  const doctors = useDoctors({ limit: 100 });
  const assignedDoctor = doctors.data?.items.find(
    (doctor) => doctor.id === patient.data?.assignedDoctorId,
  );
  const [editing, setEditing] = useState(false);
  const [booking, setBooking] = useState(false);
  const showSkeleton = useDelayedLoading(patient.isPending);
  const mayEdit = canEditPatient(can);
  const mayDelete = canDeletePatient(can);

  const { confirm, dialog } = useConfirm("patient-confirm-delete");

  const destroy = (): void => {
    const file = patient.data;

    if (!file) {
      return;
    }

    confirm({
      title: "patients.confirmDelete.title",
      titleValues: { name: file.fullName },
      consequences: [t("patients.confirmDelete.consequence")],
      onConfirm: async () => {
        try {
          await deletePatient.mutateAsync(file.id);
          toast.success("patients.deleted");
          navigate("/patients", { replace: true });
        } catch (error) {
          toast.error(...errorToast(error));
          throw error;
        }
      },
    });
  };

  return (
    <div data-testid="patient-page" className="flex flex-col gap-5">
      {dialog}
      <header
        data-testid="patient-header"
        className="border border-line rounded-card bg-surface p-4 shadow-card"
      >
        {!patient.data && <h1 className="sr-only">{t("nav.patients")}</h1>}
        {showSkeleton && <PatientHeaderSkeleton />}

        {patient.isError && (
          <p data-testid="patient-not-found" className="text-value text-danger-600">
            {t("errors.notFound")}
          </p>
        )}

        {patient.data && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar
                  name={patient.data.fullName}
                  tintKey={id}
                  size={40}
                  data-testid="patient-avatar"
                  className="text-value"
                />

                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  <h1
                    data-testid="patient-name"
                    className="truncate text-title font-medium text-primary-900"
                  >
                    {patient.data.fullName}
                  </h1>
                  <AllergyBanner patientId={id} />
                </div>
              </div>

              <div className="flex items-center gap-3">
                {canBookAppointment(can) && (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Icon name="calendar" />}
                    data-testid="patient-book"
                    onClick={() => setBooking(true)}
                  >
                    {t("appointments.create")}
                  </Button>
                )}

                {(mayEdit || mayDelete) && (
                  <RowMenu label={t("patients.rowMenu")} data-testid="patient-menu">
                    {mayEdit && (
                      <MenuItem
                        icon="edit"
                        data-testid="patient-edit"
                        onSelect={() => setEditing(true)}
                      >
                        {t("patients.edit")}
                      </MenuItem>
                    )}
                    {mayDelete && (
                      <MenuItem
                        icon="trash"
                        tone="danger"
                        data-testid="patient-delete"
                        onSelect={destroy}
                      >
                        {t("common.delete")}
                      </MenuItem>
                    )}
                  </RowMenu>
                )}
              </div>
            </div>

            <dl
              data-testid="patient-summary"
              className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-line pt-4 lg:grid-cols-5"
            >
              <div className="min-w-0">
                <dt className="text-value text-ink-muted">{t("patients.fileNumber")}</dt>
                <Ltr
                  as="dd"
                  data-testid="patient-file-number"
                  className="mt-0.5 truncate text-value text-ink tabular-nums"
                >
                  {patient.data.fileNumber}
                </Ltr>
              </div>

              <div className="min-w-0">
                <dt className="text-value text-ink-muted">{t("patients.age")}</dt>
                <dd data-testid="patient-age" className="mt-0.5 truncate text-value text-ink">
                  {patient.data.dateOfBirth
                    ? t("patients.years", { count: ageInYears(patient.data.dateOfBirth) })
                    : "—"}
                </dd>
              </div>

              <div className="min-w-0">
                <dt className="text-value text-ink-muted">{t("patients.assignedDoctor")}</dt>
                <dd
                  data-testid="patient-assigned-doctor"
                  className="mt-0.5 truncate text-value text-ink"
                >
                  {assignedDoctor ? <PersonName name={assignedDoctor.user.name} /> : "—"}
                </dd>
              </div>

              <div className="min-w-0">
                <dt className="text-value text-ink-muted">{t("patients.phone")}</dt>
                <dd className="mt-0.5 flex min-w-0 flex-col items-start gap-1 text-value text-ink">
                  <PhoneLink value={patient.data.phone} data-testid="patient-phone" />
                  <WhatsAppLink
                    value={whatsAppNumber(patient.data)}
                    label={t("patients.chatOnWhatsapp")}
                    data-testid="patient-whatsapp"
                  />
                </dd>
              </div>

              {role && canSeeBilling(can) && <PatientBalanceCard patientId={id} />}
            </dl>
          </>
        )}
      </header>

      {patient.data && (
        <PatientFormModal
          data-testid="patient-edit-modal"
          open={editing}
          onOpenChange={setEditing}
          patient={patient.data}
        />
      )}

      {patient.data && (
        <AppointmentFormModal
          data-testid="patient-book-modal"
          open={booking}
          onOpenChange={setBooking}
          forPatient={{
            id: patient.data.id,
            fullName: patient.data.fullName,
            phone: patient.data.phone,
            fileNumber: patient.data.fileNumber,
          }}
        />
      )}

      <Tabs
        data-testid="patient-tabs"
        tabs={tabs.map((tab) => ({
          id: tab.id,
          label: tab.id === "chart" ? chartTabLabel(user?.clinic.chartTypes ?? []) : tab.label,
        }))}
        value={activeTab}
        onChange={setActiveTab}
        label="patients.tabs.label"
      />

      <TabPanel id={activeTab} data-testid="patient-tab-panel">
        <Suspense fallback={<TabFallback />}>
          {activeTab === "chart" && (
            <ChartTab patientId={id} dateOfBirth={patient.data?.dateOfBirth} />
          )}
          {activeTab === "attachments" && <ImagingTab patientId={id} />}
        </Suspense>

        {activeTab === "visits" && <VisitsTab patientId={id} patient={patient.data} />}
        {activeTab === "treatmentPlans" && (
          <TreatmentPlansTab patientId={id} patient={patient.data} />
        )}
        {activeTab === "prescriptions" && (
          <PrescriptionsTab patientId={id} patient={patient.data} />
        )}
        {activeTab === "timeline" && <TimelineTab patientId={id} />}
        {activeTab === "billing" && (
          <AccountTab
            patientId={id}
            recipient={patient.data ? whatsAppNumber(patient.data) : null}
          />
        )}
      </TabPanel>
    </div>
  );
}

function TabFallback(): JSX.Element {
  return <Skeleton aria-hidden="true" className="h-[420px] w-full rounded-card" />;
}

function PatientHeaderSkeleton(): JSX.Element {
  return (
    <>
      <SkeletonStatus />

      <div aria-hidden="true">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="flex min-w-0 items-center gap-3">
            <Skeleton className="size-10 shrink-0 rounded-full" />
            <Skeleton className="h-5 w-48" />
          </div>

          <Skeleton className="h-12 w-32 rounded-panel" />
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-line pt-4 lg:grid-cols-3">
          {[0, 1, 2].map((field) => (
            <div key={field} className="min-w-0">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="mt-1.5 h-4 w-24" />
            </div>
          ))}
        </dl>
      </div>
    </>
  );
}
